import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, EMPTY, filter, Observable, switchMap, tap } from 'rxjs';
import { SelectLocationDialogComponent } from '../components/directory-tree/dialogs/select-location-dialog/select-location-dialog.component';
import { DirectoryDto } from '../data-transfer/entities/directory-dto';
import { DataExchangeRequestApiService } from '../data-transfer/services/data-exchange-request-api-service';
import { DirectoryApiService } from '../data-transfer/services/directory-api-service';
import { DialogActions } from '../model/dictionary';
import { getDialogConfig } from '../util/dialog-util';
import { SnackbarService } from './snackbar.service';

const SUCCESS_SNACKBAR_DURATION_MS = 3000;

@Injectable({ providedIn: 'root' })
export class DataExchangeSaveToDirectoryService {
  private readonly dialog = inject(MatDialog);
  private readonly directoryApiService = inject(DirectoryApiService);
  private readonly dataExchangeRequestApiService = inject(DataExchangeRequestApiService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translocoService = inject(TranslocoService);

  saveRequest(requestId: number): Observable<void> {
    return this.selectDirectoryAndSave(
      directoryId => this.dataExchangeRequestApiService.saveReceivedRequestToDirectory(requestId, directoryId, true),
      'dataManagement.dataExchangeRequests.saveToDirectory.successRequest'
    );
  }

  saveSubRequest(subRequestId: number): Observable<void> {
    return this.selectDirectoryAndSave(
      directoryId => this.dataExchangeRequestApiService.saveReceivedSubRequestToDirectory(subRequestId, directoryId, true),
      'dataManagement.dataExchangeRequests.saveToDirectory.successSubRequest'
    );
  }

  private selectDirectoryAndSave(save: (directoryId: number) => Observable<void>, successKey: string): Observable<void> {
    return this.directoryApiService.getDirectories().pipe(
      switchMap(rootFolder => {
        const dialogConfig = getDialogConfig({ rootFolder, unreachableFolders: [] as DirectoryDto[], searchable: true }, '500px');
        return this.dialog.open(SelectLocationDialogComponent, dialogConfig).afterClosed();
      }),
      filter(result => !!result && result.event !== DialogActions.REJECT),
      switchMap(result => {
        const directory: DirectoryDto = result.data;
        return save(directory.id).pipe(
          tap(() => {
            const message = this.translocoService.translate(successKey, { directory: directory.name });
            this.snackbarService.showInfo(message, SUCCESS_SNACKBAR_DURATION_MS);
          }),
          catchError(() => {
            this.snackbarService.showError('dataManagement.dataExchangeRequests.saveToDirectory.error');
            return EMPTY;
          })
        );
      })
    );
  }
}
